import { FC, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Direction_Enum, Status_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Fax } from '@weave/schema-gen-ts/dist/schemas/fax/v1/fax.pb';
import dayjs from 'dayjs';
import { FaxTypes } from '@frontend/api-fax';
import { useTimePeriodPopoverSelector } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { LocationFilterMenu } from '@frontend/location-filter-menu';
import { downloadFaxCsv } from '@frontend/media-helpers';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Chip,
  ConfirmationModal,
  ContentLoader,
  PaginationProps,
  SearchField,
  Table,
  Text,
  useDebouncedValue,
  useFormField,
  useModalControl,
  Button,
} from '@frontend/design-system';
import { useFaxActions } from '../../hooks/use-fax-actions';
import { useFaxHistory } from '../../hooks/use-fax-history';
import { ListItemStatus } from '../inbox/list-item-status';

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];

const getOutboundFaxStatus = (fax: Fax): FaxTypes.FaxStatus | undefined => {
  if (fax.direction !== Direction_Enum.OUTBOUND) return undefined;
  if (fax.status === Status_Enum.FAILED_FATAL || fax.status === Status_Enum.FAILED_RETRYABLE)
    return FaxTypes.FaxStatus.ERROR;
  if (fax.status === Status_Enum.RETRYING) return FaxTypes.FaxStatus.RETRYING;
  if (fax.status === Status_Enum.DELIVERED) return FaxTypes.FaxStatus.DELIVERY_SUCCESS;
  return FaxTypes.FaxStatus.OUTGOING_SENT;
};

export const FaxHistoryTableV2: FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation('fax');
  const { selectedLocationIds, getLocationName } = useAppScopeStore();
  const selectedFax = useRef<Fax>();
  const archiveFaxModalProps = useModalControl();
  const deleteFaxModalProps = useModalControl();
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [filterLocationIds, setFilterLocationIds] = useState<string[]>([]);

  const { archiveFax, deleteFax, previewFax } = useFaxActions();

  const searchField = useFormField({ type: 'text', value: '' });
  const searchTerm = useDebouncedValue(searchField.value, 400);

  const { startDate, endDate, TimePeriodPopoverSelector } = useTimePeriodPopoverSelector({
    defaultPeriod: 'thisWeek',
  });

  const activeLocationIds = useMemo(
    () => (filterLocationIds.length ? filterLocationIds : selectedLocationIds),
    [filterLocationIds, selectedLocationIds]
  );

  const locationOptions = useMemo(
    () => selectedLocationIds.map((id) => ({ id, name: getLocationName(id) })),
    [selectedLocationIds, getLocationName]
  );

  const startAt = startDate ? dayjs(startDate).startOf('day').toISOString() : undefined;
  const endAt = endDate ? dayjs(endDate).endOf('day').toISOString() : undefined;

  const {
    faxes,
    pageIndex,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    isLoading,
    isFetching,
    refetch,
  } = useFaxHistory({
    locationId: activeLocationIds[0] ?? selectedLocationIds[0],
    locationIds: activeLocationIds,
    startAt,
    endAt,
    searchTerm,
    pageSize,
  });

  const runAction = async (action: () => Promise<void>) => {
    setIsActionLoading(true);
    try {
      await action();
      await refetch();
    } finally {
      setIsActionLoading(false);
      selectedFax.current = undefined;
    }
  };

  const manualPaginationConfig: PaginationProps = totalPages
    ? {
        page: pageIndex + 1,
        total: totalPages,
        hasNext: hasNextPage,
        hasPrevious: hasPreviousPage,
        onNumRowsChange: setPageSize,
        defaultRowsPerPage: pageSize,
        rowsPerPageOptions: PAGE_SIZE_OPTIONS,
        showNumRowSelector: true,
        handleChange: (selectedPage: number) => {
          if (selectedPage > pageIndex + 1) goToNextPage();
          else if (selectedPage < pageIndex + 1) goToPreviousPage();
        },
      }
    : {
        page: pageIndex + 1,
        hasNext: hasNextPage,
        hasPrevious: hasPreviousPage,
        onNumRowsChange: setPageSize,
        defaultRowsPerPage: pageSize,
        rowsPerPageOptions: PAGE_SIZE_OPTIONS,
        showNumRowSelector: true,
        handleChange: (prevOrNext: 'next' | 'prev') => {
          if (prevOrNext === 'next') goToNextPage();
          else goToPreviousPage();
        },
      };

  return (
    <section css={containerStyle}>
      <div css={toolbarStyle}>
        <div css={filtersStyle}>
          <LocationFilterMenu
            locations={locationOptions}
            selectedOptions={filterLocationIds}
            onChange={setFilterLocationIds}
            onApply={setFilterLocationIds}
            showClearAll
            trackingIdBase='fax-history-v2-locations'
          />
          <TimePeriodPopoverSelector
            label={t('Time Period')}
            showTimePeriodList
            showDateRangePicker
            trackingIdBase='fax-history-v2-time-period'
          />
        </div>
        <div css={actionsStyle}>
          <div css={searchStyle}>
            <SearchField
              {...searchField}
              name='fax-history-search'
              placeholder={t('Search recipient or sender number')}
            />
          </div>
          <Button
            variant='secondary'
            iconName='export'
            trackingId='fax-history-v2-export'
            onClick={() => downloadFaxCsv(faxes, 'fax-history')}
            hoverLabel={t('Export')}
          />
          <Button
            variant='secondary'
            iconName='update'
            trackingId='fax-history-v2-refresh'
            onClick={() => refetch()}
            hoverLabel={t('Refresh')}
          />
        </div>
      </div>

      <Table
        emptyStateConfig={{ type: 'fax' }}
        colConfig={[
          {
            id: 'outboundStatus',
            Header: '',
            accessor: (fax: Fax) => fax.direction,
            disableSortBy: true,
            width: 48,
            cellRenderer: (_value, fax) => {
              const status = fax ? getOutboundFaxStatus(fax) : undefined;
              return status ? (
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                  }}
                >
                  <ListItemStatus status={status} size={16} iconSize={16} />
                </div>
              ) : null;
            },
          },
          {
            id: 'faxContact',
            Header: t('Fax Contact'),
            accessor: (fax: Fax) => fax.faxContact?.name ?? formatPhoneNumber(fax.contactPhone),
            disableSortBy: true,
            cellRenderer: (_value, fax) => {
              const name = fax?.faxContact?.name?.trim() || formatPhoneNumber(fax?.contactPhone ?? '');
              const secondName = fax?.faxContact?.secondName?.trim();
              return (
                <div css={contactCellStyle}>
                  <Avatar name={name} css={avatarStyle} />
                  <Text as='span' css={ellipsisStyle}>
                    <Text as='span' weight='bold'>
                      {name}
                    </Text>
                    {secondName ? ` ${secondName}` : ''}
                  </Text>
                </div>
              );
            },
          },
          {
            id: 'officeFaxNumber',
            Header: t('Office Fax Number'),
            accessor: (fax: Fax) => formatPhoneNumber(fax.locationPhone),
            disableSortBy: true,
            width: 160,
          },
          {
            id: 'pages',
            Header: t('Pages'),
            accessor: (fax: Fax) => fax.pages ?? 0,
            disableSortBy: true,
            width: 90,
          },
          {
            id: 'time',
            Header: t('Time'),
            accessor: (fax: Fax) => (fax.createdAt ? dayjs(fax.createdAt).format('MMM DD, YYYY, hh:mm a') : ''),
            disableSortBy: true,
            width: 200,
          },
          //TODO: add back when listfaxes shows archived faxes
          // {
          //   id: 'action',
          //   Header: t('Action'),
          //   accessor: (fax: Fax) => fax.deliveryReportMediaId ?? '',
          //   disableSortBy: true,
          //   width: 100,
          //   cellRenderer: (_value, fax) =>
          //     fax?.deliveryReportMediaId && fax.id ? (
          //       <IconButton
          //         label={t('View delivery report')}
          //         onClick={() => previewDeliveryReport(fax.id, fax.locationId)}
          //       >
          //         <Icon name='document-small' />
          //       </IconButton>
          //     ) : (
          //       <Text as='span'>–</Text>
          //     ),
          // },
          {
            id: 'location',
            Header: t('Location'),
            accessor: (fax: Fax) => getLocationName(fax.locationId ?? '') || fax.locationId,
            disableSortBy: true,
            width: 170,
            cellRenderer: (_value, fax) => {
              const locationName = getLocationName(fax?.locationId ?? '') || fax?.locationId;
              return locationName ? (
                <Chip.Location
                  hideTooltip
                  css={{
                    fontSize: theme.fontSize(12),
                    color: theme.colorsV2.black(60),
                    backgroundColor: theme.colorsV2.blue10(),
                  }}
                  className='inbox-item-chip'
                >
                  {locationName}
                </Chip.Location>
              ) : null;
            },
          },
        ]}
        data={faxes}
        hasResponsiveColWidths
        isLoading={isLoading}
        isPaginated
        manualPaginationConfig={manualPaginationConfig}
        rowActions={{
          actions: [
            {
              Icon: () => <Icon name='archive' size={16} />,
              label: t('Archive Fax'),
              onClick: (fax: Fax) => {
                selectedFax.current = fax;
                archiveFaxModalProps.openModal();
              },
            },
            {
              Icon: () => <Icon name='preview' size={16} />,
              label: t('Preview Fax'),
              onClick: (fax: Fax) => {
                if (fax.id) runAction(() => previewFax(fax.id, `fax-${fax.id}.pdf`, fax.locationId, true));
              },
            },
            {
              Icon: () => <Icon name='trash' size={16} />,
              label: t('Delete Fax'),
              onClick: (fax: Fax) => {
                selectedFax.current = fax;
                deleteFaxModalProps.openModal();
              },
            },
          ],
        }}
      />

      <ConfirmationModal
        {...archiveFaxModalProps.modalProps}
        onConfirm={() => {
          const fax = selectedFax.current;
          if (fax?.id) runAction(() => archiveFax([fax.id], fax.locationId, true));
        }}
        title={t('Are you sure you want to archive this fax?')}
      />

      <ConfirmationModal
        {...deleteFaxModalProps.modalProps}
        onConfirm={() => {
          const fax = selectedFax.current;
          if (fax?.id)
            runAction(() => deleteFax(fax.locationId, [fax.id], FaxTypes.InboxType.INBOX, [fax.locationPhone], true));
        }}
        title={t('Are you sure you want to delete this fax?')}
      />

      <ContentLoader show={isFetching || isActionLoading} />
    </section>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const toolbarStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing(2)};
  margin-bottom: ${theme.spacing(2)};
`;

const filtersStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

const searchStyle = css`
  width: 320px;
  max-width: 100%;
`;

const contactCellStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  overflow: hidden;
`;

const avatarStyle = css`
  height: ${theme.spacing(4)};
  width: ${theme.spacing(4)};
  min-width: ${theme.spacing(4)};
  min-height: ${theme.spacing(4)};
`;

const ellipsisStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
